import { useEffect, useState, type RefObject } from 'react'

import { invalidFieldSelector } from './focus-first-invalid'

const none: ReadonlySet<string> = new Set()

/**
 * The ids of the form's sections (`data-form-section`) that hold a field in error, shown or hidden,
 * so a section's tab can say it has errors (WCAG 3.3.1). Read again whenever a field's error state
 * or the form's content changes.
 */
export function useInvalidSections(formRef: RefObject<HTMLFormElement | null>): ReadonlySet<string> {
  const [invalid, setInvalid] = useState(none)

  useEffect(() => {
    const form = formRef.current
    if (form === null) {
      return undefined
    }
    const measure = () => {
      const found = new Set(
        Array.from(form.querySelectorAll(invalidFieldSelector), (field) => field.closest<HTMLElement>('[data-form-section]')?.dataset['formSection']).filter(
          (id): id is string => id !== undefined,
        ),
      )
      setInvalid((current) => (current.size === found.size && [...found].every((id) => current.has(id)) ? current : found))
    }
    const changes = new MutationObserver(measure)
    changes.observe(form, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-invalid', 'data-invalid'] })
    measure()
    return () => changes.disconnect()
  }, [formRef])

  return invalid
}
